import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Chip,
  useTheme,
  Modal,
  Fade,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Grid,
  TextField,
  Autocomplete,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../../utils/functions/api';
import EditOtForm from 'components/Forms/EditOtForm';
import OtEditCard from 'components/Card/OtEditCard';
import EditOTListTable from 'components/Table/EditOTListTable';
import getOtRateByDate from '../../utils/functions/getOtRateByDate';
import CalOthour from '../../utils/functions/CalOthour';
import * as actions from '../../redux/actions';
import { DevWinBadge } from '../DevPageBadge';

const TRAVEL_KEYWORDS = ['เดินทาง', 'ขับรถ', 'ขับมอเตอร์ไซค์'];
const getTravelAdjustedRate = (description, rate) =>
  TRAVEL_KEYWORDS.some((kw) => (description || '').includes(kw)) ? 1 : (rate || 1.5);

export default function EditOtModal({ open, onClose, id, onRefresh }) {
  const dispatch = useDispatch();
  const theme = useTheme();

  // ใช้ Redux เฉพาะ timestamp, holiday, me — ไม่แตะ otRequestOrder
  const timestamp = useSelector((state) => state.timestamp);
  const holiday = useSelector((state) => state.holiday);
  const me = useSelector((state) => state.me);

  const approverName = me?.userData?.firstname
    ? `${me.userData.firstname} ${me.userData.lastname || ''}`.trim()
    : me?.userData?.username || 'ไม่ระบุ';

  // local state สำหรับ OT order ที่ดึงตรงจาก API
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [rate, setRate] = useState(1.5);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openPersonModal, setOpenPersonModal] = useState(false);
  const [openAddPersonModal, setOpenAddPersonModal] = useState(false);
  const [formData, setFormData] = useState({
    startTime: null, endTime: null, totalHours: 0,
    rate: 1.5, totalPrice: 0, description: '', data_id: '',
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [addFormData, setAddFormData] = useState({
    startTime: dayjs().hour(17).minute(0),
    endTime: dayjs().hour(20).minute(0),
    totalHours: 0, rate: 1.5, totalPrice: 0, description: '',
  });

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/ot-request-order/${id}`,
      );
      setOrderData(data);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (open && id) fetchOrder();
  }, [open, id, fetchOrder]);

  useEffect(() => {
    if (orderData) {
      const effectiveRate = getTravelAdjustedRate(orderData?.description, orderData?.rate);
      setFormData({
        startTime: orderData?.startTime ? dayjs(orderData.startTime) : null,
        endTime: orderData?.endTime ? dayjs(orderData.endTime) : null,
        totalHours: orderData?.total_hours || 0,
        rate: effectiveRate,
        totalPrice: orderData?.total_price || 0,
        description: orderData?.description || '',
      });
      setRate(effectiveRate);

      if (orderData?.date && orderData?.project?._id) {
        dispatch(actions.timestampAll({
          dateStart: dayjs(orderData.date).startOf('day').toISOString(),
          dateEnd: dayjs(orderData.date).endOf('day').toISOString(),
          project_id: orderData?.project?._id,
        }));
      }
      if (orderData?.date) {
        dispatch(actions.holidayAll({ date: dayjs(orderData.date).startOf('date') }));
      }
    }
  }, [orderData, dispatch]);

  const autoRate = getOtRateByDate(orderData?.date, holiday?.total);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'startTime' || field === 'endTime') {
      const start = field === 'startTime' ? value : formData.startTime;
      const end = field === 'endTime' ? value : formData.endTime;
      if (start && end) {
        const hours = CalOthour(start, end);
        setFormData((prev) => ({ ...prev, totalHours: hours.toFixed(2) }));
      }
    }
    if (field === 'rate') {
      const price = formData.totalHours * (orderData?.employee?.salary?.hour || 0) * value;
      setFormData((prev) => ({ ...prev, totalPrice: price }));
    }
  };

  const handleAddFormChange = (field, value) => {
    setAddFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === 'startTime' || field === 'endTime') {
        const start = field === 'startTime' ? value : prev.startTime;
        const end = field === 'endTime' ? value : prev.endTime;
        if (start && end) {
          const hours = CalOthour(start, end);
          newData.totalHours = hours.toFixed(2);
          newData.totalPrice = hours * (selectedEmployee?.salary?.hour || 0) * newData.rate;
        }
      }
      if (field === 'rate') {
        const hours = parseFloat(prev.totalHours || 0);
        newData.totalPrice = hours * (selectedEmployee?.salary?.hour || 0) * value;
      }
      return newData;
    });
  };

  const handleSubmit = async () => {
    const data = {
      startTime: formData.startTime?.toISOString(),
      endTime: formData.endTime?.toISOString(),
      total_hours: formData.totalHours,
      rate: formData.rate,
      total_price: formData.totalPrice,
      description: formData.description,
    };
    await api.put(`${process.env.REACT_APP_API_URL}/ot-request-order/${id}`, data);
    await fetchOrder();
    setOpenEditModal(false);
  };

  const handleEdit = (data) => {
    const effectiveRate = getTravelAdjustedRate(data?.description, data?.rate);
    setFormData({
      startTime: data?.startTime ? dayjs(data.startTime) : null,
      endTime: data?.endTime ? dayjs(data.endTime) : null,
      totalHours: data?.total_hours || 0,
      rate: effectiveRate,
      totalPrice: data?.total_price || 0,
      description: data?.description || '',
      data_id: data?._id || '',
    });
    setRate(effectiveRate);
    setOpenPersonModal(true);
  };

  const handleSubmitEditPerson = async () => {
    const data = {
      startTime: formData.startTime?.toISOString(),
      endTime: formData.endTime?.toISOString(),
      total_hours: formData.totalHours,
      rate: formData.rate,
      total_price: formData.totalPrice,
      description: formData.description,
      _edit_context: 'approver_edit',
      _edited_by_name: approverName,
    };
    await dispatch(actions.otRequestPut(formData?.data_id, data));
    await fetchOrder();
    setOpenPersonModal(false);
  };

  const handleSubmitAddPerson = async () => {
    if (!selectedEmployee) { alert('กรุณาเลือกพนักงาน'); return; }
    const start = addFormData.startTime;
    const end = addFormData.endTime;
    const hours = CalOthour(start, end);
    const data = {
      order: id,
      timestamp: selectedEmployee?._id,
      employee: selectedEmployee?.employee?._id,
      employee_name: `${selectedEmployee?.employee?.firstname} ${selectedEmployee?.employee?.lastname}`,
      department: selectedEmployee?.employee?.department?.name || '',
      startTime: start?.toISOString(),
      endTime: end?.toISOString(),
      total_hours: hours,
      rate: addFormData.rate,
      total_price: hours * (selectedEmployee?.salary?.hr || 0) * addFormData.rate,
      description: addFormData.description,
      salary: selectedEmployee?.salary || {},
      project: selectedEmployee?.project_in || {},
      status: orderData?.status,
    };
    await dispatch(actions.otRequestCreate(data));
    await fetchOrder();
    setOpenAddPersonModal(false);
  };

  const handleDeletePerson = async (dataId) => {
    if (window.confirm('ยืนยันการลบข้อมูล?')) {
      await dispatch(actions.otRequestDelete(dataId));
      await fetchOrder();
    }
  };

  const handleClose = () => {
    onRefresh?.();
    onClose();
  };

  const availableEmployees =
    timestamp?.rows?.filter((ts) =>
      !orderData?.ot_lists?.some((ot) => ot?.timestamp === ts?._id),
    ) || [];

  const otTypes = [
    { value: 1, label: 'โอทีวันเดินทาง', rate: '1x', color: 'default' },
    { value: 1.5, label: 'โอทีวันปกติ (จ–ส)', rate: '1.5x', color: 'warning' },
    { value: 3.0, label: 'โอทีวันหยุด/อาทิตย์', rate: '3x', color: 'error' },
  ];

  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    timeOptions.push(`${h.toString().padStart(2, '0')}:00`);
    timeOptions.push(`${h.toString().padStart(2, '0')}:30`);
  }
  const toDayjsTime = (str) => {
    const [hour, minute] = str.split(':');
    return dayjs().hour(Number(hour)).minute(Number(minute)).second(0);
  };

  return (
    <>
    <DevWinBadge num={141} name="EditOtModal" open={open} />
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          p: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>แก้ไข OT</Typography>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mt: 1 }}>
              <OtEditCard
                otRequestOrder={orderData}
                rate={rate}
                setRate={setRate}
                handleOpenModal={() => setOpenEditModal(true)}
                handleEdit={handleEdit}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedEmployee(null);
                    setAddFormData({
                      startTime: dayjs().hour(17).minute(0),
                      endTime: dayjs().hour(20).minute(0),
                      rate: autoRate,
                      description: orderData?.description || '',
                    });
                    setOpenAddPersonModal(true);
                  }}
                  disabled={availableEmployees.length === 0}
                >
                  เพิ่มพนักงาน
                </Button>
              </Stack>
              <EditOTListTable
                otRequestOrder={orderData}
                handleEdit={handleEdit}
                handleDeletePerson={handleDeletePerson}
              />
            </Box>
          </>
        )}
      </DialogContent>

      {/* Edit Order Modal */}
      <Modal open={openEditModal} onClose={() => setOpenEditModal(false)} closeAfterTransition
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Fade in={openEditModal}>
          <Card sx={{ width: { xs: '95%', sm: '90%', md: 600 }, maxHeight: '90vh', overflow: 'auto', borderRadius: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={700}>แก้ไขข้อมูล OT</Typography>
                <IconButton onClick={() => setOpenEditModal(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
              </Box>
              <Box sx={{ p: 3 }}>
                <EditOtForm formData={formData} handleChange={handleChange}
                  handleCloseModal={() => setOpenEditModal(false)} handleSubmit={handleSubmit} />
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Modal>

      {/* Edit Person Modal */}
      <Modal open={openPersonModal} onClose={() => setOpenPersonModal(false)} closeAfterTransition
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Fade in={openPersonModal}>
          <Card sx={{ width: { xs: '95%', sm: '90%', md: 600 }, maxHeight: '90vh', overflow: 'auto', borderRadius: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={700}>แก้ไขข้อมูลรายบุคคล OT</Typography>
                <IconButton onClick={() => setOpenPersonModal(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
              </Box>
              <Box sx={{ p: 3 }}>
                <EditOtForm formData={formData} handleChange={handleChange}
                  handleCloseModal={() => setOpenPersonModal(false)} handleSubmit={handleSubmitEditPerson} />
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Modal>

      {/* Add Person Modal */}
      <Modal open={openAddPersonModal} onClose={() => setOpenAddPersonModal(false)} closeAfterTransition
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Fade in={openAddPersonModal}>
          <Card sx={{ width: { xs: '95%', sm: '90%', md: 600 }, maxHeight: '90vh', overflow: 'auto', borderRadius: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`, color: 'white', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonAddIcon />
                  <Typography variant="h6" fontWeight={700}>เพิ่มพนักงานเข้า OT</Typography>
                </Stack>
                <IconButton onClick={() => setOpenAddPersonModal(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete options={availableEmployees}
                      getOptionLabel={(option) => `${option?.employee?.firstname} ${option?.employee?.lastname} (${option?.employee?.department?.name})`}
                      value={selectedEmployee} onChange={(_, value) => setSelectedEmployee(value)}
                      renderInput={(params) => <TextField {...params} label="เลือกพนักงาน" required size="small" placeholder="พิมพ์เพื่อค้นหา" />}
                      noOptionsText="ไม่มีพนักงานที่สามารถเพิ่มได้" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">เวลาทำงาน OT</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField select label="เวลาเริ่ม OT" fullWidth size="small"
                      value={addFormData.startTime.format('HH:mm')}
                      onChange={(e) => handleAddFormChange('startTime', toDayjsTime(e.target.value))}>
                      {timeOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField select label="เวลาสิ้นสุด OT" fullWidth size="small"
                      value={addFormData.endTime.format('HH:mm')}
                      onChange={(e) => handleAddFormChange('endTime', toDayjsTime(e.target.value))}>
                      {timeOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>อัตรา OT</InputLabel>
                      <Select value={addFormData.rate} label="อัตรา OT"
                        onChange={(e) => handleAddFormChange('rate', e.target.value)}>
                        {otTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Stack direction="row" justifyContent="space-between" width="100%">
                              <Typography variant="body2">{type.label}</Typography>
                              <Chip label={type.rate} size="small" color={type.color} sx={{ fontWeight: 600 }} />
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="หมายเหตุ" value={addFormData.description} fullWidth multiline rows={3} size="small"
                      onChange={(e) => handleAddFormChange('description', e.target.value)}
                      placeholder="เหตุผลในการขอ OT" />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => setOpenAddPersonModal(false)}>ยกเลิก</Button>
                      <Button variant="contained" color="success" startIcon={<SaveIcon />}
                        onClick={handleSubmitAddPerson} disabled={!selectedEmployee}>เพิ่ม</Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Modal>
    </Dialog>
    </>
  );
}
